import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Shield, Award, CheckCircle, TrendingUp, Users, BookOpen, ClipboardCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { StandardsGrid } from '../components/training/StandardsGrid';

export const ManagementSystems = () => {
  const navigate = useNavigate();

  // Static content for SEO + AEO + GEO optimization
  const categoryData = {
    title: 'ISO Training Services',
    description: 'ISO Training Services play a critical role in helping organizations understand, implement, maintain, and improve international management system standards. From quality and environmental management to information security and occupational health & safety, ISO training equips professionals with the knowledge and skills required to meet global standards confidently.',
    meta_title: 'ISO Training Services - Lead Auditor, Internal Auditor & Awareness Programs | ISDE Academy',
    meta_description: 'ISO Training Services for ISO 9001, 14001, 45001, 27001, 13485, 22000, 50001, 21001, IATF 16949. Expert-led Lead Auditor, Internal Auditor, and Awareness training programs. Build compliance, audit readiness, and continuous improvement capabilities.',
    meta_keywords: 'ISO training services, ISO lead auditor training, ISO internal auditor training, ISO awareness training, ISO 9001 training, ISO 14001 training, ISO 45001 training, ISO 27001 training, ISO 13485 training, ISO 22000 training, ISO 50001 training, ISO 21001 training, IATF 16949 training, ISO certification training India, Chennai ISO training, management system training, ISO audit training'
  };

  // Structured data for SEO + AEO (Answer Engine Optimization)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "ISDE Academy",
    "description": categoryData.description,
    "url": "https://isde.academy",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Chennai",
      "addressRegion": "Tamil Nadu",
      "addressCountry": "India"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "ISO Training Services",
      "itemListElement": [
        { name: "ISO 9001:2015", title: "Quality Management System" },
        { name: "ISO 14001:2015", title: "Environmental Management System" },
        { name: "ISO 45001:2018", title: "Occupational Health & Safety" },
        { name: "ISO 27001:2022", title: "Information Security Management" },
        { name: "ISO 13485:2016", title: "Medical Devices Quality Management" },
        { name: "ISO 22000:2018", title: "Food Safety Management System" },
        { name: "ISO 50001:2018", title: "Energy Management System" },
        { name: "ISO 21001:2018", title: "Educational Organizations Management" },
        { name: "IATF 16949:2016", title: "Automotive Quality Management" }
      ].map((std, index) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Course",
          "name": `${std.name} - ${std.title}`,
          "provider": {
            "@type": "Organization",
            "name": "ISDE Academy",
            "url": "https://isde.academy"
          }
        }
      }))
    }
  };

  // FAQ structured data for AEO (Answer Engine Optimization)
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Why are ISO Training Services important?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ISO Training Services improve compliance with international standards, enhance audit readiness, reduce non-conformities and operational risks, build internal auditing capabilities, strengthen organizational credibility, and support continual improvement and business excellence."
        }
      },
      {
        "@type": "Question",
        "name": "What are the benefits of ISO Training Services for organizations?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Key benefits include improved process efficiency, enhanced risk management, better audit performance, employee skill development, and stronger customer confidence through certified and well-trained teams."
        }
      },
      {
        "@type": "Question",
        "name": "How do ISO Training Services help individuals?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ISO training offers higher employability across industries, opportunities as internal auditors or consultants, global recognition of certification, better understanding of compliance and governance, and leadership roles in quality and compliance functions."
        }
      }
    ]
  };

  const importanceReasons = [
    'Improves compliance with international standards',
    'Enhances audit readiness and confidence',
    'Reduces non-conformities and operational risks',
    'Builds internal auditing and implementation capabilities',
    'Strengthens organizational credibility and customer trust',
    'Supports continual improvement and business excellence'
  ];

  const orgBenefits = [
    { title: 'Improved Process Efficiency', description: 'ISO training enables teams to streamline processes, reduce waste, and improve productivity through systematic approaches.', icon: TrendingUp },
    { title: 'Enhanced Risk Management', description: 'Training emphasizes risk identification, assessment, and mitigation, helping organizations prevent issues before they occur.', icon: Shield },
    { title: 'Better Audit Performance', description: 'Trained professionals are well-prepared for internal and external audits, resulting in fewer non-conformities.', icon: ClipboardCheck },
    { title: 'Employee Skill Development', description: 'ISO training builds professional competence, boosting confidence and accountability across departments.', icon: BookOpen },
    { title: 'Stronger Customer Confidence', description: 'Certified and well-trained organizations gain trust from customers, regulators, and stakeholders.', icon: Users }
  ];

  const careerAdvantages = [
    'Higher employability across industries',
    'Opportunities as internal auditors or consultants',
    'Global recognition of certification',
    'Better understanding of compliance and governance',
    'Leadership roles in quality and compliance functions'
  ];

  return (
    <>
      <Helmet>
        <title>{categoryData.meta_title}</title>
        <meta name="description" content={categoryData.meta_description} />
        <meta name="keywords" content={categoryData.meta_keywords} />
        <link rel="canonical" href="https://isde.academy/training/iso-training-services" />

        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={categoryData.meta_title} />
        <meta property="og:description" content={categoryData.meta_description} />
        <meta property="og:url" content="https://isde.academy/training/iso-training-services" />
        <meta property="og:site_name" content="ISDE Academy" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={categoryData.meta_title} />
        <meta name="twitter:description" content={categoryData.meta_description} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>

        {/* FAQ Structured Data for AEO */}
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 text-white py-24 mt-20 overflow-hidden">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-4000"></div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute top-10 right-10 opacity-20">
            <Shield className="h-32 w-32" />
          </div>
          <div className="absolute bottom-10 left-10 opacity-20">
            <Award className="h-24 w-24" />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <motion.div
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Shield className="h-5 w-5 text-blue-200" />
                  <span className="text-sm font-semibold text-blue-100">ISO & IATF Certified Training</span>
                </motion.div>

                <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                  {categoryData.title}
                </h1>
                <p className="text-xl text-blue-100 mb-4 leading-relaxed">
                  {categoryData.description}
                </p>
                <p className="text-lg text-blue-200 mb-8 leading-relaxed">
                  ISO training is not just about passing audits, it is about building a culture of continuous improvement, risk-based thinking, and sustainable growth.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-4">
                  <Button
                    onClick={() => navigate('/contact')}
                    className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                  >
                    Get Started
                  </Button>
                  <Button
                    onClick={() => navigate('/training-calendar')}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold px-8 py-6 text-lg"
                  >
                    View Schedule
                  </Button>
                </div>
              </motion.div>

              {/* Right Content - Stats Cards */}
              <motion.div
                className="grid grid-cols-2 gap-4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <motion.div
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="text-4xl font-bold mb-2">9</div>
                  <div className="text-blue-100 text-sm">ISO/IATF Standards</div>
                </motion.div>

                <motion.div
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="text-4xl font-bold mb-2">3</div>
                  <div className="text-blue-100 text-sm">Training Programs</div>
                </motion.div>

                <motion.div
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="text-4xl font-bold mb-2">10+</div>
                  <div className="text-blue-100 text-sm">Years Experience</div>
                </motion.div>

                <motion.div
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="text-4xl font-bold mb-2">5000+</div>
                  <div className="text-blue-100 text-sm">Professionals Trained</div>
                </motion.div>
              </motion.div>
            </div>
          </div>

          {/* Bottom Wave */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F9FAFB" />
            </svg>
          </div>
        </section>

        {/* Why ISO Training Services Are Important */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
                Why ISO Training Services Are Important
              </h2>
              <p className="text-lg text-gray-600 max-w-4xl mx-auto text-center mb-10 leading-relaxed">
                ISO standards are globally recognized frameworks that help organizations ensure quality, safety, efficiency, and compliance. However, simply obtaining certification is not enough. Proper understanding and effective implementation require trained personnel.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
                Key Reasons to Invest in ISO Training Services
              </h3>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto mb-8">
                {importanceReasons.map((reason, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{reason}</span>
                  </motion.div>
                ))}
              </div>

              <p className="text-lg text-gray-600 max-w-4xl mx-auto text-center italic">
                ISO training services ensure that standards are applied practically, not just documented for audits.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Benefits of ISO Training Services for Organizations */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10 text-center">
                Benefits of ISO Training Services for Organizations
              </h3>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {orgBenefits.map((benefit, index) => {
                  const IconComponent = benefit.icon;
                  return (
                    <motion.div
                      key={index}
                      className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="bg-blue-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                        <IconComponent className="h-6 w-6 text-blue-600" />
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        {benefit.title}
                      </h4>
                      <p className="text-gray-600 leading-relaxed">
                        {benefit.description}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ISO Training Services for Individuals */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto"
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 text-center">
                ISO Training Services for Individuals
              </h3>
              <p className="text-lg text-gray-600 text-center mb-8 leading-relaxed">
                ISO training is also highly beneficial for professionals seeking career growth.
              </p>

              <h4 className="text-xl font-semibold text-gray-800 mb-6 text-center">
                Career Advantages Include
              </h4>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {careerAdvantages.map((advantage, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{advantage}</span>
                  </motion.div>
                ))}
              </div>

              <p className="text-lg text-gray-700 text-center font-medium bg-blue-50 rounded-xl p-6 border border-blue-100">
                ISO Lead Auditor and Internal Auditor certifications are particularly valuable in today's job market.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Standards Grid - Material Design Cards */}
        <StandardsGrid />

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Get ISO Certified?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join thousands of professionals who have enhanced their careers with our ISO training programs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/contact')}
                className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8 py-3 text-lg"
              >
                Enroll Now
              </Button>
              <Button
                onClick={() => navigate('/training-calendar')}
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-blue-600 font-semibold px-8 py-3 text-lg"
              >
                View Schedule
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
